// ./src/hooks/useTags.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import {
  Tag,
  TagService,
  TagFilter,
  TagInput,
  Pagination,
} from "@/utils/api";
import { SortItem, listPage } from "@/utils/api";
import { upsertBy, upsertManyBy, removeBy, removeManyBy } from "@/utils";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { getNextTagColor } from "@/contexts/TagsContext/tagColors";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

// ============================================
// Types
// ============================================

export interface UseTagsOptions {
  /**
   * Page size for pagination.
   * Mount-time configuration — changing after mount has no effect.
   */
  pageSize?: number;

  /**
   * Filters applied on initial autoLoad and userId-change reloads.
   * Mount-time configuration — call list(newFilters) to change.
   */
  initialFilters?: Partial<TagFilter>;

  /**
   * Sort applied on initial autoLoad and userId-change reloads.
   * Mount-time configuration — pass sort to list() to change.
   */
  initialSort?: SortItem[];

  /** Auto-load data on mount (and on userId change) */
  autoLoad?: boolean;
}

export interface UseTagsReturn {
  // Data state
  tag: Tag | null;
  tags: Tag[];
  loading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  // Pagination state
  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  /** IDs currently being removed — supports many concurrent removals. */
  removingIds: Set<string>;
  /** True when `id` is being removed (or, with no id, when any removal is in flight). */
  isRemoving: (id?: string) => boolean;
  selectedTag: Tag | null;

  // Filter and sort
  filters: Partial<TagFilter>;
  sortBy: SortItem[];

  // Core operations
  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<TagFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: TagInput) => Promise<OpResult>;
  update: (id: string, params: TagInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;

  // Convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  // UI state management
  setSelectedTag: (tag: Tag | null) => void;

  // Helper operations
  reset: () => void;

  // Lookup helpers
  getTagById: (id: string | undefined) => Tag | undefined;
  getTagsByIds: (ids: string[]) => Tag[];

  // Derived data
  activeTags: Tag[];
  totalTagsCount: number;
  activeTagsCount: number;
  hasTags: boolean;

  // Color helpers
  getNextAutoColor: () => string;

  // Inline creation (for TagsSelectorInput)
  createTagInline: (tagName: string) => Promise<Tag | null>;

  // Error handling
  lastError: OpResult | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_SORT: SortItem[] = [{ name: "name", order: "ASC" }];

// The API tag filter is { id, searchKeyword } — there is no status filter
// (the server scopes the list to the account's active tags). Keep this empty.
const DEFAULT_FILTERS: Partial<TagFilter> = {};

// ============================================
// Hook Implementation
// ============================================

export const useTags = (options: UseTagsOptions = {}): UseTagsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = true,
  } = options;

  // Get user from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  // ── Mount-time configuration captured in refs ──
  const pageSizeRef = useRef(pageSize);
  // The page size of the LAST read: a refresh re-runs that read, so a WHOLE_SET read stays whole (#118).
  const lastPageSizeRef = useRef<number>(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  // Ref to track the last-queried filters/sort for loadMore/refresh
  const lastQueriedFilterRef = useRef<Partial<TagFilter>>(initialFilters);
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  // Monotonically increasing call ID to detect stale list() responses
  const listCallIdRef = useRef(0);

  // Track previous userId for change detection
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Data state
  const [tag, setTag] = useState<Tag | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state — "No notes
   * yet", "No tasks yet" — before the skeleton and then the data. Reported across the whole event
   * space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters — tracks what was last queried
  const [filters, setFilters] = useState<Partial<TagFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<OpResult | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => isLoaded && tags.length === 0,
    [isLoaded, tags.length]
  );

  // ==========================================
  // Removal tracking (supports many at once)
  // ==========================================

  const startRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const stopRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  const isRemoving = useCallback(
    (id?: string): boolean => (id ? removingIds.has(id) : removingIds.size > 0),
    [removingIds]
  );

  // ==========================================
  // Helpers
  // ==========================================

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    listCallIdRef.current++;

    setTag(null);
    setTags([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingIds(new Set());
    setSelectedTag(null);
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  const get = useCallback(async (id: string): Promise<OpResult> => {
    setLoading(true);
    setLastError(null);

    try {
      const result = await TagService.get(id);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = (result.data as Tag[])[0];
        setTag(data);
        setTags((prev) => upsertBy(prev, data));
        setLastError(null);
        return result;
      }

      setLastError(result);
      setTag(null);
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);
      setTag(null);
      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * List tags.
   * Race-condition safe via listCallIdRef.
   */
  const list = useCallback(
    async (
      filterOverride: Partial<TagFilter> = {},
      pagination?: Pagination,
      sortByOverride?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      lastPageSizeRef.current = targetPageSize;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
        setCurrentPage(1);
      }

      setLastError(null);

      // Store queried filters/sort in refs
      setFilters(filterOverride);
      lastQueriedFilterRef.current = filterOverride;

      if (sortByOverride) {
        setSortBy(sortByOverride);
        lastQueriedSortRef.current = sortByOverride;
      }

      const currentSortBy = sortByOverride || lastQueriedSortRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const result = await listPage(
          TagService.list,
          filterOverride as TagFilter,
          paginationParams,
          currentSortBy
        );

        // Guard stale responses
        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as Tag[])
            : [];

          if (isAppending) {
            setTags((prev) => upsertManyBy(prev, newItems, "id", "end"));
          } else {
            setTags(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setTags([]);
        }

        return result;
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setTags([]);
        }

        return result;
      } finally {
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  const getMany = useCallback(async (ids: string[]): Promise<OpResult> => {
    if (ids.length === 0) {
      return new OpResult();
    }

    setLoading(true);
    setLastError(null);

    try {
      const result = await TagService.getMany(ids);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const loadedItems = Array.isArray(result.data)
          ? (result.data as Tag[])
          : [];
        setTags((prev) => upsertManyBy(prev, loadedItems));
        setLastError(null);
        return result;
      }

      setLastError(result);
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);
      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  // ==========================================
  // Mutation Operations
  // ==========================================

  const create = useCallback(async (params: TagInput): Promise<OpResult> => {
    setIsCreating(true);
    setLastError(null);

    try {
      const result = await TagService.create(params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const newItem = (result.data as Tag[])[0];
        setTag(newItem);
        setTags((prev) => upsertBy(prev, newItem));
        setLastError(null);
        return result;
      }

      setLastError(result);
      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);
      return result;
    } finally {
      setIsCreating(false);
    }
  }, []);

  const update = useCallback(
    async (id: string, params: TagInput): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Tag ID is required for updating"
        );
        setLastError(result);
        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await TagService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as Tag[])[0];
          setTag(updatedItem);
          setTags((prev) => upsertBy(prev, updatedItem));
          setLastError(null);
          return result;
        }

        setLastError(result);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Tag ID is required for removal"
        );
        setLastError(result);
        return result;
      }

      startRemoving([id]);
      setLastError(null);

      try {
        const result = await TagService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setTag(null);
          setTags((prev) => removeBy(prev, id));
          setLastError(null);
          return result;
        }

        setLastError(result);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);
        return result;
      } finally {
        stopRemoving([id]);
      }
    },
    [startRemoving, stopRemoving]
  );

  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "At least one tag ID is required for removal"
        );
        setLastError(result);
        return result;
      }

      startRemoving(ids);
      setLastError(null);

      try {
        const result = await TagService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setTag(null);
          setTags((prev) => removeManyBy(prev, ids));
          setLastError(null);
          return result;
        }

        setLastError(result);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);
        return result;
      } finally {
        stopRemoving(ids);
      }
    },
    [startRemoving, stopRemoving]
  );

  // ==========================================
  // Convenience Methods
  // ==========================================

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    const nextPage = currentPage + 1;

    await list(
      lastQueriedFilterRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loading, loadingMore, hasMore, currentPage]);

  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFilterRef.current,
      { page: 1, pageSize: lastPageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(refresh, isLoaded);

  // ==========================================
  // Lookup Helpers
  // ==========================================

  const getTagById = useCallback(
    (id: string | undefined): Tag | undefined => {
      if (!id) {
        return undefined;
      }
      return tags.find((item) => item.id === id);
    },
    [tags]
  );

  const getTagsByIds = useCallback(
    (ids: string[]): Tag[] => {
      if (!ids || ids.length === 0) {
        return [];
      }
      return ids
        .map((id) => tags.find((tag) => tag.id === id))
        .filter((tag): tag is Tag => tag !== undefined);
    },
    [tags]
  );

  // ==========================================
  // Derived Data
  // ==========================================

  const activeTags = useMemo(
    () => tags.filter((tag) => tag.status === STATUSES.ACTIVE),
    [tags]
  );

  const totalTagsCount = useMemo(() => tags.length, [tags]);

  const activeTagsCount = useMemo(() => activeTags.length, [activeTags]);

  const hasTags = useMemo(() => activeTags.length > 0, [activeTags]);

  // ==========================================
  // Color Helpers
  // ==========================================

  const getNextAutoColor = useCallback((): string => {
    return getNextTagColor(tags.length);
  }, [tags.length]);

  // ==========================================
  // Inline Creation (for TagsSelectorInput)
  // ==========================================

  /**
   * Create a tag inline with auto-assigned color.
   * Checks for duplicates (case-insensitive) before creating.
   * Returns the created or existing Tag, or null on failure.
   */
  const createTagInline = useCallback(
    async (tagName: string): Promise<Tag | null> => {
      const trimmedName = tagName.trim();

      if (!trimmedName) {
        return null;
      }

      // Check if tag with same name already exists (case-insensitive)
      const existingTag = tags.find(
        (tag) =>
          tag.name?.toLowerCase() === trimmedName.toLowerCase() &&
          tag.status === STATUSES.ACTIVE
      );

      if (existingTag) {
        return existingTag;
      }

      const color = getNextTagColor(tags.length);

      const params: TagInput = {
        name: trimmedName,
        color,
      };

      const result = await create(params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        return (result.data as Tag[])[0] || null;
      }

      return null;
    },
    [tags, create]
  );

  // ==========================================
  // Effects
  // ==========================================

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }
      return;
    }

    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      tag,
      tags,
      loading,
      isLoaded,
      isEmpty,

      // Pagination
      loadingMore,
      hasMore,
      currentPage,
      pageSize: pageSizeRef.current,

      // Operations state
      isCreating,
      isUpdating,
      removingIds,
      isRemoving,
      selectedTag,

      // Filter and sort
      filters,
      sortBy,

      // Core operations
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,

      // Convenience methods
      loadMore,
      refresh,

      // UI state management
      setSelectedTag,

      // Helper operations
      reset,

      // Lookup helpers
      getTagById,
      getTagsByIds,

      // Derived data
      activeTags,
      totalTagsCount,
      activeTagsCount,
      hasTags,

      // Color helpers
      getNextAutoColor,

      // Inline creation
      createTagInline,

      // Error handling
      lastError,
      clearError,
    }),
    [
      tag,
      tags,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      isCreating,
      isUpdating,
      removingIds,
      isRemoving,
      selectedTag,
      filters,
      sortBy,
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,
      loadMore,
      refresh,
      reset,
      getTagById,
      getTagsByIds,
      activeTags,
      totalTagsCount,
      activeTagsCount,
      hasTags,
      getNextAutoColor,
      createTagInline,
      lastError,
      clearError,
    ]
  );
};
